// components/shared/CostCalculator.jsx
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { DB } from '../../utils/db';

const MEDICATION_PRICES = {
  'Lisinopril 10mg': { retail: 85.00, medicare: 45.00, direct: 12.00 },
  'Metformin 500mg': { retail: 72.00, medicare: 38.00, direct: 9.00 },
  'Atorvastatin 20mg': { retail: 145.00, medicare: 78.00, direct: 18.00 },
  'Omeprazole 20mg': { retail: 95.00, medicare: 52.00, direct: 14.00 },
  'Amlodipine 5mg': { retail: 68.00, medicare: 35.00, direct: 11.00 }
};

export function CostCalculator({ user, onNavigate }) {
  const { brand } = useBrand();
  const [selectedMeds, setSelectedMeds] = useState([]);
  const [customMed, setCustomMed] = useState({ name: '', retail: '', medicare: '', direct: '' });
  const [showCustomForm, setShowCustomForm] = useState(false);
  
  useEffect(() => {
    loadUserMeds();
  }, [user?.id]);
  
  const loadUserMeds = async () => {
    if (!user) return;
    
    const prescriptions = await DB.get('d2p_prescriptions') || [];
    const userRxs = prescriptions.filter(rx => 
      rx.userId === user.id && 
      rx.status === 'verified'
    );
    
    const medsWithPrices = userRxs
      .map(rx => {
        const prices = MEDICATION_PRICES[rx.medication];
        if (prices) {
          return { name: rx.medication, ...prices };
        }
        return null;
      })
      .filter(Boolean);
    
    if (medsWithPrices.length > 0) {
      setSelectedMeds(medsWithPrices);
    }
  };
  
  const toggleMed = (medName) => {
    const prices = MEDICATION_PRICES[medName];
    const isSelected = selectedMeds.some(m => m.name === medName);
    
    if (isSelected) {
      setSelectedMeds(prev => prev.filter(m => m.name !== medName));
    } else {
      setSelectedMeds(prev => [...prev, { name: medName, ...prices }]);
    }
  };
  
  const addCustomMed = () => {
    if (!customMed.name || !customMed.retail || !customMed.direct) return;
    
    const med = {
      name: customMed.name,
      retail: parseFloat(customMed.retail),
      medicare: parseFloat(customMed.medicare) || parseFloat(customMed.retail) * 0.6,
      direct: parseFloat(customMed.direct)
    };
    
    setSelectedMeds(prev => [...prev, med]);
    setCustomMed({ name: '', retail: '', medicare: '', direct: '' });
    setShowCustomForm(false);
  };
  
  const totals = selectedMeds.reduce(
    (acc, med) => ({
      retail: acc.retail + med.retail,
      medicare: acc.medicare + med.medicare,
      direct: acc.direct + med.direct
    }),
    { retail: 0, medicare: 0, direct: 0 }
  );
  
  const monthlySavings = totals.retail - totals.direct;
  const annualSavings = monthlySavings * 12;
  const savingsPercent = totals.retail > 0 ? Math.round((monthlySavings / totals.retail) * 100) : 0;
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <Button
          variant="ghost"
          icon="arrowLeft"
          onClick={() => onNavigate('dashboard')}
          style={{ marginBottom: 24 }}
        >
          Back to Dashboard
        </Button>
        
        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
          Cost Savings Calculator
        </h1>
        <p style={{ color: '#6B7280', marginBottom: 32 }}>
          Compare {brand.name} direct pricing with retail pharmacy and Medicare Part D costs
        </p>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 24,
          alignItems: 'start'
        }}>
          {/* Left Column - Medication Selection */}
          <div>
            <Card style={{ marginBottom: 16 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, color: brand.c.navy }}>
                Select Medications
              </h2>
              
              <div style={{ display: 'grid', gap: 12 }}>
                {Object.keys(MEDICATION_PRICES).map(medName => {
                  const isSelected = selectedMeds.some(m => m.name === medName);
                  return (
                    <label
                      key={medName}
                      style={{
                        padding: 12,
                        border: `2px solid ${isSelected ? brand.c.accent : '#E5E7EB'}`,
                        borderRadius: 8,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        background: isSelected ? `${brand.c.accent}05` : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleMed(medName)}
                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{medName}</span>
                    </label>
                  );
                })}
              </div>
              
              <Button
                variant="secondary"
                size="sm"
                icon="plus"
                onClick={() => setShowCustomForm(!showCustomForm)}
                style={{ marginTop: 16 }}
                fullWidth
              >
                {showCustomForm ? 'Cancel' : 'Add Custom Medication'}
              </Button>
              
              {showCustomForm && (
                <div style={{
                  marginTop: 16,
                  padding: 16,
                  background: '#F9FAFB',
                  borderRadius: 8
                }}>
                  <input
                    type="text"
                    value={customMed.name}
                    onChange={e => setCustomMed({ ...customMed, name: e.target.value })}
                    placeholder="Medication name"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #E5E7EB',
                      borderRadius: 6,
                      fontSize: 14,
                      marginBottom: 12,
                      boxSizing: 'border-box',
                      fontFamily: 'Libre Franklin, sans-serif'
                    }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                    <input
                      type="number"
                      value={customMed.retail}
                      onChange={e => setCustomMed({ ...customMed, retail: e.target.value })}
                      placeholder="Retail $"
                      style={{
                        padding: '8px 10px',
                        border: '2px solid #E5E7EB',
                        borderRadius: 6,
                        fontSize: 13,
                        fontFamily: 'Libre Franklin, sans-serif'
                      }}
                    />
                    <input
                      type="number"
                      value={customMed.medicare}
                      onChange={e => setCustomMed({ ...customMed, medicare: e.target.value })}
                      placeholder="Medicare $"
                      style={{
                        padding: '8px 10px',
                        border: '2px solid #E5E7EB',
                        borderRadius: 6,
                        fontSize: 13,
                        fontFamily: 'Libre Franklin, sans-serif'
                      }}
                    />
                    <input
                      type="number"
                      value={customMed.direct}
                      onChange={e => setCustomMed({ ...customMed, direct: e.target.value })}
                      placeholder="Direct $"
                      style={{
                        padding: '8px 10px',
                        border: '2px solid #E5E7EB',
                        borderRadius: 6,
                        fontSize: 13,
                        fontFamily: 'Libre Franklin, sans-serif'
                      }}
                    />
                  </div>
                  <Button size="sm" onClick={addCustomMed} fullWidth>
                    Add to Calculation
                  </Button>
                </div>
              )}
            </Card>
            
            {selectedMeds.length > 0 && (
              <Card>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: brand.c.navy }}>
                  Selected Medications ({selectedMeds.length})
                </h3>
                <div style={{ display: 'grid', gap: 8 }}>
                  {selectedMeds.map((med, i) => (
                    <div
                      key={i}
                      style={{
                        padding: 10,
                        background: '#F9FAFB',
                        borderRadius: 6,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{med.name}</span>
                      <button
                        onClick={() => setSelectedMeds(prev => prev.filter((_, idx) => idx !== i))}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4
                        }}
                      >
                        <Icon name="x" size={16} color="#9CA3AF" />
                      </button>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
          
          {/* Right Column - Cost Comparison */}
          <div>
            {selectedMeds.length === 0 ? (
              <Card>
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <Icon name="dollar" size={32} color="#9CA3AF" />
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                    Select Medications
                  </h3>
                  <p style={{ color: '#6B7280', fontSize: 14 }}>
                    Choose medications from the list to see your potential savings
                  </p>
                </div>
              </Card>
            ) : (
              <>
                <Card style={{ marginBottom: 16 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
                    Monthly Cost Comparison
                  </h2>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 12,
                    marginBottom: 24
                  }}>
                    <div style={{
                      padding: 16,
                      background: '#FEE2E2',
                      borderRadius: 8,
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: 12, color: '#991B1B', marginBottom: 4, fontWeight: 600 }}>
                        Retail Pharmacy
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 900, color: '#EF4444' }}>
                        ${totals.retail.toFixed(2)}
                      </div>
                    </div>
                    
                    <div style={{
                      padding: 16,
                      background: '#FEF3C7',
                      borderRadius: 8,
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: 12, color: '#92400E', marginBottom: 4, fontWeight: 600 }}>
                        Medicare Part D
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 900, color: '#F59E0B' }}>
                        ${totals.medicare.toFixed(2)}
                      </div>
                    </div>
                    
                    <div style={{
                      padding: 16,
                      background: '#D1FAE5',
                      borderRadius: 8,
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: 12, color: '#065F46', marginBottom: 4, fontWeight: 600 }}>
                        {brand.name} Direct
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 900, color: '#10B981' }}>
                        ${totals.direct.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{
                    padding: 16,
                    background: '#EFF6FF',
                    borderRadius: 8
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontSize: 14, color: '#1E40AF', marginBottom: 4, fontWeight: 600 }}>
                          Monthly Savings
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 900, color: '#3B82F6' }}>
                          ${monthlySavings.toFixed(2)}
                        </div>
                      </div>
                      <div style={{
                        padding: '8px 16px',
                        background: brand.c.accent,
                        borderRadius: 8,
                        color: 'white',
                        fontSize: 20,
                        fontWeight: 700
                      }}>
                        {savingsPercent}%
                      </div>
                    </div>
                  </div>
                </Card>
                
                <Card style={{
                  background: `linear-gradient(135deg, ${brand.c.accent}, ${brand.c.navy})`,
                  color: 'white'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 10,
                      background: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon name="trendUp" size={24} color="white" />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>
                        Annual Savings Projection
                      </div>
                      <div style={{ fontSize: 36, fontWeight: 900, fontFamily: "'Merriweather', serif" }}>
                        ${annualSavings.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <p style={{ fontSize: 14, opacity: 0.9, lineHeight: 1.6 }}>
                    By using {brand.name}, you could save ${annualSavings.toFixed(2)} per year 
                    compared to retail pharmacy pricing. That's enough for {Math.floor(annualSavings / 50)} 
                    extra copays or several months of groceries!
                  </p>
                </Card>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}